import type { AskHelenaTicketRef } from "shared";

const TICKET_PATH = "/app/service-desk/tickets/";

const ticketUrl = (ticketId: string): string =>
  `${window.location.origin}${TICKET_PATH}${encodeURIComponent(ticketId)}`;

/**
 * Elements whose TEXT must be left alone: an `<a>` already links somewhere, and
 * a ticket number inside `<code>`/`<pre>` is being quoted, not referenced.
 */
const SKIP_CONTENT_TAGS = new Set(["a", "code", "pre"]);

const TAG = /<\/?([a-zA-Z][^\s/>]*)[^>]*>/g;

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const escapeAttribute = (value: string): string =>
  value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

/**
 * A ticket number ends where a word character or hyphen does — so "TKT-10" does
 * not match inside "TKT-1042", and a number already carrying a suffix is left
 * whole rather than half-linked.
 */
const isBoundary = (char: string | undefined): boolean =>
  char === undefined || !/[\w-]/.test(char);

const toTicketIds = (
  ticketRefs: AskHelenaTicketRef[] | null | undefined,
): Map<string, string> => {
  const ticketIds = new Map<string, string>();

  for (const ref of ticketRefs ?? []) {
    if (ref?.number && ref?.id) ticketIds.set(ref.number.toLowerCase(), ref.id);
  }

  return ticketIds;
};

/**
 * Longest first, so an overlapping pair ("TKT-10", "TKT-104") matches the whole
 * number rather than its prefix.
 */
const toNumberPattern = (ticketIds: Map<string, string>): RegExp =>
  new RegExp(
    [...ticketIds.keys()]
      .sort((a, b) => b.length - a.length)
      .map(escapeRegExp)
      .join("|"),
    "gi",
  );

const anchor = (ticketId: string, label: string): string =>
  `<a href="${escapeAttribute(ticketUrl(ticketId))}">${label}</a>`;

const linkifyText = (
  text: string,
  ticketIds: Map<string, string>,
  pattern: RegExp,
): string => {
  pattern.lastIndex = 0;

  let result = "";
  let cursor = 0;
  let match = pattern.exec(text);

  while (match) {
    const start = match.index;
    const end = start + match[0].length;
    const ticketId = ticketIds.get(match[0].toLowerCase());

    if (ticketId && isBoundary(text[start - 1]) && isBoundary(text[end])) {
      result += text.slice(cursor, start) + anchor(ticketId, match[0]);
      cursor = end;
    }

    match = pattern.exec(text);
  }

  return result + text.slice(cursor);
};

/**
 * Turns every ticket number the service resolved into a link to that ticket.
 *
 * Works on HTML rather than the Markdown it came from, because that is the one
 * place both render paths meet — prose through `markdownToHtml`, table cells
 * through `inlineMarkdownToHtml`. It also means a number the author already
 * linked, or wrote inside a code block, is visible as such and can be skipped.
 *
 * The scan is string-level on purpose: a DOM round-trip would decode the `&#10;`
 * entities `markdownToHtml` puts inside `<pre>`, and SafeHtmlRenderer turns a
 * real newline there into a `<br>` — double-spacing every code block. Tags are
 * copied through byte for byte; only the text between them is rewritten.
 *
 * DOMPurify still runs after this, in SafeHtmlRenderer and in
 * `inlineMarkdownToHtml`, and both force `target="_blank"` onto every link — so
 * a ticket opens in a new tab and the conversation stays where it is.
 */
export const linkifyTicketRefs = (
  html: string,
  ticketRefs: AskHelenaTicketRef[] | null | undefined,
): string => {
  const ticketIds = toTicketIds(ticketRefs);
  if (ticketIds.size === 0) return html;

  const pattern = toNumberPattern(ticketIds);

  let result = "";
  let cursor = 0;
  let skipDepth = 0;

  TAG.lastIndex = 0;
  let tag = TAG.exec(html);

  while (tag) {
    const text = html.slice(cursor, tag.index);
    result += skipDepth > 0 ? text : linkifyText(text, ticketIds, pattern);
    result += tag[0];
    cursor = tag.index + tag[0].length;

    if (SKIP_CONTENT_TAGS.has(tag[1].toLowerCase()) && !tag[0].endsWith("/>")) {
      skipDepth = tag[0].startsWith("</")
        ? Math.max(0, skipDepth - 1)
        : skipDepth + 1;
    }

    tag = TAG.exec(html);
  }

  const tail = html.slice(cursor);
  return (
    result + (skipDepth > 0 ? tail : linkifyText(tail, ticketIds, pattern))
  );
};
